import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
  trackApplication,
  verifyApplicationRecovery,
} from '@/api/onboarding';
import {
  disableNetBanking,
  getLoginHistory,
  listUserDevices,
  listUserSessions,
  resendTemporaryPassword,
  resetPassword,
  revokeUserDevice,
  revokeUserSession,
  startPasswordRecovery,
  trustUserDevice,
  type LoginHistoryPage,
  type UserDevice,
  type UserSession,
} from '@/api/auth';
import { setSession } from '@/api/session';
import { features } from '@/api/flags';
import { isEasyPin } from '@/utils/phase2Validation';
import Button from '@/components/ui/Button/Button';
import OtpInput from '@/components/ui/OtpInput/OtpInput';

const passwordRules = (value: string) => [
  ['8 to 20 characters', value.length >= 8 && value.length <= 20],
  ['Upper and lower case letters', /[a-z]/.test(value) && /[A-Z]/.test(value)],
  ['At least one number', /\d/.test(value)],
  ['At least one special character', /[^A-Za-z0-9]/.test(value)],
] as const;

const PasswordField = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => {
  const [visible, setVisible] = useState(false);
  return <div className="mb-3">
    <label className="form-label">{label}</label>
    <div className="input-group">
      <input className="form-control" maxLength={20} type={visible ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" className="btn btn-outline-secondary" onClick={() => setVisible((v) => !v)}>{visible ? 'Hide' : 'Show'}</button>
    </div>
  </div>;
};

export const TrackApplication = () => {
  const navigate = useNavigate();
  const [reference, setReference] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const track = async () => {
    const value = reference.trim().toUpperCase();
    const mobileNumber = mobile.replace(/\D/g, '');
    if (!value) return setError('Enter your application reference.');
    if (mobileNumber.length < 10) return setError('Enter the registered mobile number.');
    try {
      const recovery = await trackApplication({ applicationReference: value, mobileNumber });
      navigate('/track/verify', {
        state: {
          recoveryId: recovery.recoveryId,
          maskedDestination: recovery.maskedDestination,
          mobileNumber,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't find that application.");
    }
  };
  return <div className="container py-5" style={{ maxWidth: 620 }}><h1>Track my application</h1><p className="text-muted">Enter your application reference to view its status.</p><label className="form-label">Application reference</label><input className="form-control mb-3" placeholder="NB-2026-027617" value={reference} onChange={(e) => setReference(e.target.value.toUpperCase())} /><label className="form-label">Registered mobile number</label><input className="form-control mb-3" inputMode="tel" placeholder="9876543210" value={mobile} onChange={(e) => setMobile(e.target.value)} />{error && <div className="alert alert-danger">{error}</div>}<Button onClick={() => void track()}>Track</Button>{features.trackByMobile && <button className="btn btn-link d-block mt-3" onClick={() => navigate('/track/verify')}>Track with my mobile number</button>}</div>;
};

export const TrackVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { recoveryId?: string; maskedDestination?: string; mobileNumber?: string } | null;
  const recoveryId = state?.recoveryId;
  const maskedDestination = state?.maskedDestination;
  const mobileNumber = state?.mobileNumber;
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const verify = async () => {
    if (!recoveryId) return setError('Start application tracking again to request a new code.');
    setVerifying(true);
    setError('');
    try {
      const application = await verifyApplicationRecovery({ recoveryId, otp: code });
      setSession({
        type: 'TRACKING',
        reference: application.applicationReference,
        mobileNumber,
      });
      toast.success('Application verified');
      navigate('/applicant');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The verification code is incorrect.');
    } finally {
      setVerifying(false);
    }
  };

  return <div className="container py-5" style={{ maxWidth: 620 }}><h1>Verify tracking</h1><p>Enter the 6-digit OTP sent to {maskedDestination || 'your registered contact'}.</p><OtpInput value={code} onChange={setCode} error={error} /><Button disabled={verifying || code.length !== 6} onClick={() => void verify()}>{verifying ? 'Verifying…' : 'Verify'}</Button></div>;
};

export const TemporaryPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!email.includes('@')) return setError('Enter the email you gave when you opened the account.');
    if (!dob) return setError('Choose your date of birth.');
    try {
      await resendTemporaryPassword(email, dob);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a temporary password.');
    }
  };
  return <div className="container py-5" style={{ maxWidth: 620 }}><h1>New temporary password</h1>{sent ? <><h2>Check your email</h2><p>If those details match an approved account, a new temporary password is on its way. It is valid for 72 hours.</p><Button onClick={() => navigate('/login')}>Back to login</Button></> : <><p>We emailed you a temporary password when your account was approved. If it expired or you lost it, we can send a new one.</p><label className="form-label">Email</label><input className="form-control mb-3" value={email} onChange={(e) => setEmail(e.target.value)} /><label className="form-label">Date of birth</label><input className="form-control mb-3" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />{error && <div className="alert alert-danger">{error}</div>}<Button onClick={submit}>Send</Button></>}</div>;
};

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [step, setStep] = useState(1);
  const [transactionId, setTransactionId] = useState('');
  const [error, setError] = useState('');
  const rules = passwordRules(password);
  const next = async () => {
    setError('');
    try {
      if (step === 1) {
        if (!identifier.trim()) return setError('Enter your email, Customer ID or mobile.');
        const value = identifier.trim();
        const digits = value.replace(/\D/g, '');
        const mobileNumber = digits.length === 12 && digits.startsWith('91') ? `+${digits}` : digits;
        const recovery = await startPasswordRecovery(
          value.includes('@')
            ? { email: value }
            : /^[6-9]\d{9}$/.test(mobileNumber.replace(/^\+91/, ''))
              ? { mobileNumber: mobileNumber.startsWith('+') ? mobileNumber : `+91${mobileNumber}` }
              : { customerId: value },
        );
        setTransactionId(recovery.transactionId);
        return setStep(2);
      }
      if (step === 2) {
        if (code.length !== 6) return setError('Enter the 6-digit OTP.');
        return setStep(3);
      }
      if (!rules.every(([, ok]) => ok)) return setError('Use upper and lower case, a number and a special character.');
      if (password !== confirm) return setError('Passwords do not match.');
      await resetPassword({ transactionId, code, newPassword: password });
      toast.success('Password reset. Log in with your new password.');
      navigate('/login', { replace: true, state: { notice: 'Password reset. Log in with your new password.' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password recovery failed. Please try again.');
    }
  };
  return <div className="container py-5" style={{ maxWidth: 620 }}><h1>Forgot password</h1>{step === 1 && <><label className="form-label">Email, Customer ID or mobile</label><input className="form-control" value={identifier} onChange={(e) => setIdentifier(e.target.value)} /></>}{step === 2 && <><p>Enter the OTP sent to your registered mobile.</p><OtpInput value={code} onChange={setCode} error={error} /></>}{step === 3 && <><PasswordField label="New password" value={password} onChange={setPassword} /><ul>{rules.map(([label, ok]) => <li key={label} className={ok ? 'text-success' : 'text-muted'}>{ok ? '✓' : '○'} {label}</li>)}</ul><PasswordField label="Confirm password" value={confirm} onChange={setConfirm} /></>}{error && <div className="alert alert-danger mt-3">{error}</div>}<Button onClick={() => void next()}>{step === 3 ? 'Reset password' : 'Continue'}</Button></div>;
};

export const ForgotUsername = () => {
  const [mobile, setMobile] = useState('');
  const [dob, setDob] = useState('');
  const [result, setResult] = useState('');
  return <div className="container py-5" style={{ maxWidth: 620 }}><h1>Forgot username</h1>{result ? <p className="alert alert-success">{result}</p> : <><label className="form-label">Mobile number</label><input className="form-control mb-3" value={mobile} onChange={(e) => setMobile(e.target.value)} /><label className="form-label">Date of birth</label><input className="form-control mb-3" type="date" value={dob} onChange={(e) => setDob(e.target.value)} /><Button onClick={() => setResult("Your username is ra****@example.com. We've also sent it to your registered mobile.")}>Find my username</Button></>}</div>;
};

export const PinSettings = ({ kind }: { kind: 'MPIN' | 'UPI' }) => {
  const navigate = useNavigate();
  const [pinLength, setPinLength] = useState<4 | 6>(6);
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const submit = () => {
    if (pin.length !== pinLength || confirm.length !== pinLength) {
      return setError(`Enter your new ${pinLength}-digit ${kind}.`);
    }
    if (pin !== confirm) return setError(`${kind} PINs do not match.`);
    if (isEasyPin(pin)) return setError('That PIN is too easy to guess.');
    toast.success(`${kind} PIN set.`);
    navigate(-1);
  };
  const updatePin = (value: string, setter: (value: string) => void) =>
    setter(value.replace(/\D/g, '').slice(0, pinLength));
  return (
    <div className="container py-5" style={{ maxWidth: 620 }}>
      <h1>{kind === 'UPI' ? 'Set UPI PIN' : 'Set MPIN'}</h1>
      <p>Choose a PIN length, then enter it twice.</p>
      <div className="btn-group mb-3" role="group" aria-label={`${kind} length`}>
        {[4, 6].map((length) => (
          <Button
            key={length}
            variant={pinLength === length ? 'primary' : 'outline'}
            onClick={() => {
              setPinLength(length as 4 | 6);
              setPin('');
              setConfirm('');
              setError('');
            }}
          >
            {length} digits
          </Button>
        ))}
      </div>
      <input className="form-control mb-3" inputMode="numeric" maxLength={pinLength} type="password" value={pin} onChange={(e) => updatePin(e.target.value, setPin)} placeholder={`Enter new ${kind} PIN`} />
      <input className="form-control mb-3" inputMode="numeric" maxLength={pinLength} type="password" value={confirm} onChange={(e) => updatePin(e.target.value, setConfirm)} placeholder={`Confirm ${kind} PIN`} />
      {error && <div className="alert alert-danger">{error}</div>}
      <Button onClick={submit}>Save</Button>
    </div>
  );
};

export const SecurityList = ({ devices = false }: { devices?: boolean }) => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [deviceList, setDeviceList] = useState<UserDevice[]>([]);
  const [history, setHistory] = useState<LoginHistoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [disablingNetBanking, setDisablingNetBanking] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      if (devices) {
        setDeviceList(await listUserDevices());
      } else {
        const [activeSessions, loginHistory] = await Promise.all([listUserSessions(), getLoginHistory()]);
        setSessions(activeSessions);
        setHistory(loginHistory);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load security details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [devices]);

  const revokeSession = async (id: number) => {
    try {
      await revokeUserSession(id);
      toast.success('Session signed out.');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not sign out this session.');
    }
  };

  const revokeDevice = async (id: number) => {
    try {
      await revokeUserDevice(id);
      toast.success('Device forgotten.');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not revoke this device.');
    }
  };

  const trustDevice = async (id: number) => {
    try {
      await trustUserDevice(id);
      toast.success('Device trusted.');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not trust this device.');
    }
  };

  const handleDisableNetBanking = async () => {
    if (!window.confirm('Turn off net banking? App and web sign-in will stop until a branch re-enables it.')) return;
    setDisablingNetBanking(true);
    try {
      await disableNetBanking();
      toast.success('Net banking has been turned off.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not turn off net banking.');
    } finally {
      setDisablingNetBanking(false);
    }
  };

  const formatDate = (value: string) => new Date(value).toLocaleString();

  return (
    <div className="container py-5" style={{ maxWidth: 760 }}>
      <h1>{devices ? 'Known devices' : 'Active sessions'}</h1>
      {!devices && <Button variant="outline" disabled={disablingNetBanking} onClick={() => void handleDisableNetBanking()}>
        {disablingNetBanking ? 'Turning off…' : 'Turn off net banking'}
      </Button>}
      {loading && <p>Loading…</p>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !devices && sessions.map((session) => (
        <div className="card p-3 mb-3" key={session.id}>
          <strong>{session.current ? 'Current session' : session.userAgent || 'Browser session'}</strong>
          <p className="text-muted mb-2">{session.ipAddress} · Last active {formatDate(session.lastAccessedAt)}</p>
          <Button variant="outline" disabled={session.current} onClick={() => void revokeSession(session.id)}>
            {session.current ? 'Current session' : 'Sign out'}
          </Button>
        </div>
      ))}
      {!loading && devices && deviceList.map((device) => (
        <div className="card p-3 mb-3" key={device.id}>
          <strong>{device.deviceName || device.browser || 'Registered device'}</strong>
          <p className="text-muted mb-2">{device.operatingSystem} · {device.status} · Last used {formatDate(device.lastUsedAt)}</p>
          {device.status !== 'TRUSTED' && <Button variant="outline" onClick={() => void trustDevice(device.id)}>Trust</Button>}
          <Button variant="outline" onClick={() => void revokeDevice(device.id)}>Forget</Button>
        </div>
      ))}
      {!loading && !devices && history && history.content.length > 0 && <>
        <h2 className="h5 mt-4">Login history</h2>
        {history.content.map((entry, index) => <div className="border-bottom py-2" key={String(entry.id ?? index)}>{String(entry.eventType ?? entry.action ?? 'Sign-in')} · {String(entry.ipAddress ?? '')}</div>)}
      </>}
      {!loading && ((devices && deviceList.length === 0) || (!devices && sessions.length === 0)) && <p className="text-muted">No records found.</p>}
    </div>
  );
};
